'use client';

import { useEffect, useState } from 'react';
import { BackendAuth } from '@/lib/auth-backend';
import { setUserTimezone } from '@/lib/datetime';
import { isTrialSession } from '@/lib/trial-session';

// v0.6.1: Synchronous SSO token injection for Xoffi-OS Desktop BG-Window.
// Electron passes the token via ?xoffi_sso_token= URL param. We must inject it
// into localStorage SYNCHRONOUSLY (before any component renders) so that the
// ProtectedLayout auth check finds a valid token on its first render.
// This runs at module import time (server-side: no-op, client-side: injects token).
let _ssoTokenFromUrl = null;
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('xoffi_sso_token');
    if (ssoToken && typeof ssoToken === 'string' && ssoToken.length > 20) {
      _ssoTokenFromUrl = ssoToken;
      const existing = localStorage.getItem('auth_token');
      if (!existing || existing !== ssoToken) {
        localStorage.setItem('auth_token', ssoToken);
        localStorage.setItem('access_token', ssoToken);
      }
    }
  } catch (_) {/* localStorage may be unavailable in some contexts */}
}

// AuthProvider - Initializes CSRF token protection on app startup
// BackendAuth verwendet localStorage statt NextAuth Sessions
export function AuthProvider({ children }) {
  // v0.6.4: When SSO token comes via URL (Xoffi-OS BG-Window), user_data is not
  // yet in localStorage → BackendAuth.isAuthenticated() returns false → HomePage
  // redirects to /signin → useWebPush never runs registerFCM. Fix: bootstrap
  // user_data from /api/users/me synchronously (block render) before children mount.
  const needsBootstrap =
    typeof window !== 'undefined' &&
    !!_ssoTokenFromUrl &&
    !localStorage.getItem('user_data');
  const [bootstrapDone, setBootstrapDone] = useState(!needsBootstrap);

  useEffect(() => {
    let cancelled = false;

    // Anzeige-Zeitzone aus dem bereits gecachten Profil setzen — synchron, ohne
    // Netzwerk, damit die erste Render-Runde schon in der richtigen Zone formatiert.
    // Ohne Wert bleibt der Helper bei der Browser-Zone (= gewünschter Default).
    try {
      const cached = JSON.parse(localStorage.getItem('user_data') || 'null');
      if (cached?.timezone) setUserTimezone(cached.timezone);
    } catch (_) { /* kein/ungültiger Cache → Browser-Zone */ }

    const run = async () => {
      try {
        if (needsBootstrap) {
          try {
            const res = await fetch('/api/users/me', {
              headers: { Authorization: `Bearer ${_ssoTokenFromUrl}` },
              credentials: 'include',
            });
            if (res.ok) {
              const userData = await res.json();
              try {
                localStorage.setItem('user_data', JSON.stringify(userData));
              } catch (_) {}
              // Anzeige-Zeitzone sofort scharfstellen (users.timezone kommt in /users/me mit).
              if (userData?.timezone) setUserTimezone(userData.timezone);
            }
          } catch (e) {
            console.warn('AuthProvider: /api/users/me bootstrap failed:', e?.message);
          }
        }
        try {
          await BackendAuth.fetchCsrfToken();
        } catch (error) {
          console.error('Failed to initialize CSRF token:', error);
        }

        // Timezone auto-detect: once per session, fire-and-forget. Runs after the
        // CSRF token is available (POST is state-changing). The server only applies
        // the value when users.timezone IS NULL — it never overrides an explicit
        // profile choice — so sending it on every fresh session is safe.
        try {
          if (
            BackendAuth.isAuthenticated() &&
            !isTrialSession() && // website-setup trial: endpoint answers 403 TRIAL_FORBIDDEN
            !sessionStorage.getItem('xoffi_tz_checked')
          ) {
            sessionStorage.setItem('xoffi_tz_checked', '1');
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz) {
              BackendAuth.apiCallJson('/api/user/profile/timezone-autodetect', {
                method: 'POST',
                body: JSON.stringify({ timezone: tz }),
              }).catch(() => { /* best-effort; server NULL-guard is authoritative */ });
            }
          }
        } catch (_) { /* sessionStorage / Intl unavailable — ignore */ }
      } finally {
        if (!cancelled) setBootstrapDone(true);
      }
    };
    run();

    // Refresh CSRF token periodically (every 3 hours, token expires in 4)
    const refreshInterval = setInterval(() => {
      BackendAuth.fetchCsrfToken();
    }, 3 * 60 * 60 * 1000); // 3 hours

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
  }, []);

  // Block render only on initial SSO-from-URL bootstrap to prevent the "no user_data
  // → redirect to /signin" race. For all other entry points (regular login, already
  // authenticated, anonymous), render immediately like before.
  if (!bootstrapDone) return null;

  return children;
}
